import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useTheme, useMediaQuery, Tooltip, Typography, Button, Box, Card, CardContent, Grid, FormControl, MenuItem, InputLabel, Select, TextField, Modal, Paper, IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Info as InfoIcon,
  Upload as UploadIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { NavLink } from 'react-router-dom';
import { useDrawer } from '../../../components/context/DrawerContext';
import { stickyListRegionWidth } from '../../../components/layouts/StickyListToolbar';

import { Client } from '../../../models/client';
import { Collect } from '../../../models/collect';
import { Office } from '../../../models/office';
import { useSelection } from '../../../components/context/SelectionContext';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import Title from '../../../components/Title';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn } from '../../../components/table/interface';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import moment from 'moment-timezone';
import { Constant } from '../../../models/constant';
import { formatCurrency } from '../../../utils/format/currency';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const normalizeKey = (str: string) =>
  str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const excelDateToJS = (excelDate: number) =>
  moment(new Date(Math.round((excelDate - 25569 + 1) * 86400 * 1000))).format('YYYY-MM-DD');

const parseNumericValue = (value: unknown) => {
  if (value === undefined || value === null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const normalizedValue = String(value)
    .trim()
    .replace(/\s+/g, '')
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.');
  const parsedValue = Number(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const hasMeaningfulValue = (value: unknown) => {
  if (value === undefined || value === null) return false;
  return String(value).trim() !== '';
};

const getPeriodCount = (termInDays: number, typeConstantId?: number | null) => {
  if (!Number.isFinite(termInDays) || termInDays <= 0) return 1;
  if (typeConstantId === 2) return Math.max(1, Math.round((termInDays / 30) * 4));
  if (typeConstantId === 3) return Math.max(1, Math.round(termInDays / 15));
  if (typeConstantId === 4) return Math.max(1, Math.round(termInDays / 30));
  return termInDays;
};

const getImportErrorMessage = (error: any) => {
  const responseData = error?.response?.data;

  if (typeof responseData?.message === 'string' && responseData.message.trim() !== '') {
    return responseData.message;
  }

  if (Array.isArray(responseData) && responseData.length > 0) {
    const firstDetail = responseData[0];
    if (typeof firstDetail?.message === 'string' && firstDetail.message.trim() !== '') {
      return firstDetail.message;
    }
  }

  if (typeof error?.message === 'string' && error.message.trim() !== '') {
    return error.message;
  }

  return 'Error al guardar los préstamos';
};

const getImportErrors = (error: any) => {
  const responseErrors = error?.response?.data?.errors;
  return Array.isArray(responseErrors)
    ? responseErrors.filter((item: unknown) => typeof item === 'string' && item.trim() !== '')
    : [];
};

const getImportErrorsFromResponse = (data: any, includeMessageFallback = false) => {
  const responseErrors = Array.isArray(data?.errors)
    ? data.errors.filter((item: unknown) => typeof item === 'string' && item.trim() !== '')
    : [];

  if (responseErrors.length > 0) {
    return responseErrors;
  }

  const responseDetails = Array.isArray(data?.details)
    ? data.details.filter((item: unknown) => typeof item === 'string' && item.trim() !== '')
    : [];

  if (responseDetails.length > 0) {
    return responseDetails;
  }

  if (includeMessageFallback && typeof data?.message === 'string' && data.message.trim() !== '') {
    return [data.message];
  }

  return [];
};

const getFirstValue = (row: Record<string, unknown>, aliases: string[]) => {
  for (const alias of aliases) {
    const value = row[alias];
    if (hasMeaningfulValue(value)) {
      return value;
    }
  }
  return '';
};

const isValidImportedDate = (value: unknown) => {
  if (!hasMeaningfulValue(value)) return false;
  return moment(String(value), 'YYYY-MM-DD', true).isValid();
};

const buildImportedRows = (
  rows: any[],
  utility: string,
  daysTerm: string,
  typeConstantId?: number | string,
  constantId?: number | string
) => {
  const term = parseNumericValue(daysTerm);
  const numericTypeConstantId = Number(typeConstantId || 0);
  const periodCount = getPeriodCount(term, numericTypeConstantId);
  const isWeekly = numericTypeConstantId === 2;

  return rows.map((row: any, idx: number) => {
    const idnumber = getFirstValue(row, ['cedula', 'cédula', 'documento', 'identificacion', 'identificación', 'idnumber']);
    const fullname = getFirstValue(row, ['nombre', 'nombres', 'nombre cliente', 'cliente', 'fullname']);
    const cellphone = getFirstValue(row, ['telefono', 'teléfono', 'celular', 'movil', 'móvil', 'cellphone']);
    const mainaddress = getFirstValue(row, ['direccion', 'dirección', 'direccion domicilio', 'direccion cliente', 'mainaddress']);
    const loanDate = getFirstValue(row, ['fecha', 'fecha prestamo', 'fecha préstamo', 'fecha credito', 'fecha crédito', 'date']);
    const normalizedLoanDate = typeof loanDate === 'number' ? loanDate : String(loanDate);
    const utilityFromConstant = utility !== '' ? parseNumericValue(utility) : null;
    const utilityFromFile = parseNumericValue(
      row['utilidad'] ??
      row['% utilidad'] ??
      row['porcentaje utilidad'] ??
      row['porcentaje de utilidad']
    );
    const utilidad = utilityFromConstant ?? utilityFromFile;
    const total = parseNumericValue(row['valor prestamo']);
    const balance = parseNumericValue(row['saldo']);
    const paid_total = Math.max(total - balance, 0);
    const daily_pay = periodCount > 0
      ? Number((total / periodCount).toFixed(2))
      : 0;
    const capital = utilidad >= 0 ? total / (1 + utilidad / 100) : total;
    const utilidadValor = Math.round(total - capital);
    const countPayFromFile = parseNumericValue(
      row['count_pay'] ??
      row['no pagos'] ??
      row['no. pagos'] ??
      row['numero de pagos'] ??
      row['n pagos']
    );
    const count_pay = countPayFromFile > 0
      ? countPayFromFile
      : daily_pay > 0 && paid_total > 0
        ? Math.ceil(paid_total / daily_pay)
        : 0;
    const count_efective_pay = daily_pay > 0
      ? Math.floor(paid_total / daily_pay)
      : 0;

    return {
      id: idx,
      rowNumber: idx + 1,
      position: parseNumericValue(row['posicion']),
      idnumber,
      fullname,
      cellphone,
      mainaddress,
      total_value: total,
      daily_pay_value: daily_pay,
      value: Math.round(capital),
      value_utility: utilidadValor,
      utility: utilidad,
      date: typeof normalizedLoanDate === 'number'
        ? excelDateToJS(normalizedLoanDate)
        : moment(normalizedLoanDate, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY', moment.ISO_8601], true).isValid()
          ? moment(normalizedLoanDate, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY', moment.ISO_8601], true).format('YYYY-MM-DD')
          : moment(normalizedLoanDate).format('YYYY-MM-DD'),
      balance,
      paid_total,
      count_pay,
      count_efective_pay,
      constant_id: constantId ?? null,
      is_weekly: isWeekly,
      route_day: '',
    };
  });
};

const recalculateRowWithConstant = (row: any, constant: Constant) => {
  const term = parseNumericValue(constant.term);
  const utilidad = parseNumericValue(constant.utility);
  const typeConstantId = Number(constant.type_constant.id);
  const weekly = typeConstantId === 2;
  const periodCount = getPeriodCount(term, typeConstantId);
  const total = parseNumericValue(row.total_value);
  const balance = parseNumericValue(row.balance);
  const paid_total = Math.max(total - balance, 0);
  const daily_pay = periodCount > 0
    ? Number((total / periodCount).toFixed(2))
    : 0;
  const capital = utilidad >= 0 ? total / (1 + utilidad / 100) : total;
  const utilidadValor = Math.round(total - capital);
  const count_pay = daily_pay > 0 && paid_total > 0
    ? Math.ceil(paid_total / daily_pay)
    : 0;
  const count_efective_pay = daily_pay > 0
    ? Math.floor(paid_total / daily_pay)
    : 0;

  return {
    ...row,
    constant_id: constant.id,
    is_weekly: weekly,
    route_day: weekly ? row.route_day ?? '' : '',
    utility: utilidad,
    total_value: total,
    daily_pay_value: daily_pay,
    value: Math.round(capital),
    value_utility: utilidadValor,
    balance,
    paid_total,
    count_pay,
    count_efective_pay,
  };
};

const validateImportedRows = (rows: any[]) => {
  const validationErrors: string[] = [];

  rows.forEach((row: any, idx: number) => {
    const rowNumber = Number(row?.rowNumber ?? idx + 1);
    const missingFields: string[] = [];

    if (!hasMeaningfulValue(row?.idnumber)) missingFields.push('cedula');
    if (!hasMeaningfulValue(row?.fullname)) missingFields.push('nombre');
    if (!hasMeaningfulValue(row?.mainaddress)) missingFields.push('direccion');
    if (!hasMeaningfulValue(row?.date)) missingFields.push('fecha');

    if (missingFields.length > 0) {
      validationErrors.push(
        `Fila ${rowNumber}: faltan campos obligatorios (${missingFields.join(', ')}). ` +
        'Revisa esa fila en el Excel antes de guardar.'
      );
      return;
    }

    if (!isValidImportedDate(row?.date)) {
      validationErrors.push(
        `Fila ${rowNumber}: la fecha "${row?.date}" no es válida. Revisa el formato de la celda en el Excel.`
      );
    }
  });

  return validationErrors;
};

const errorModalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 900,
  height: 620,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const ImportarPrestamos: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = "Importar Préstamos";
  const { drawerOpen } = useDrawer();
  const { canCreate, canRead, canViewErrors } = useResourcePermissions('/procesos/importarprestamo');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [fileRows, setFileRows] = useState<any[]>([]);
  const [rawFileRows, setRawFileRows] = useState<any[]>([]);
  const [constants, setConstants] = useState<Constant[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [days_term, setDays_term] = useState("");
  const [utility, setUtility] = useState("");
  const [selectedConstantIsWeekly, setSelectedConstantIsWeekly] = useState(false);
  const [selectedTypeConstantId, setSelectedTypeConstantId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Si no reseteamos el input file, algunos navegadores no disparan `onChange`
  // al intentar seleccionar otro archivo (o incluso el mismo).
  const [fileInputKey, setFileInputKey] = useState(0);
  const { office_id, setOffice_id, collect_id, setCollect_id, user_id } = useSelection();
  const [Constantid, setConstantid] = useState('');
  const selectedPaymentCount = getPeriodCount(
    parseNumericValue(days_term),
    selectedTypeConstantId ?? undefined,
  );
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileRows([]);
    setRawFileRows([]);
    setImportErrors([]);
    setErrorModalOpen(false);
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    try {

      showLoading();
      reader.onload = (evt) => {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawJson = XLSX.utils.sheet_to_json(sheet);

        // Renombrar claves normalizadas
        const renamed = rawJson.map((row: any) => {
          const rowNorm: any = {};
          for (const key in row) {
            const newKey = normalizeKey(key); // ejemplo: "valor prestamo"
            rowNorm[newKey] = row[key];
          }
          return rowNorm;
        }).filter((row: Record<string, unknown>) => {
          const requiredCandidates = [
            getFirstValue(row, ['cedula', 'cédula', 'documento', 'identificacion', 'identificación', 'idnumber']),
            getFirstValue(row, ['nombre', 'nombres', 'nombre cliente', 'cliente', 'fullname']),
            getFirstValue(row, ['direccion', 'dirección', 'direccion domicilio', 'direccion cliente', 'mainaddress']),
            getFirstValue(row, ['fecha', 'fecha prestamo', 'fecha préstamo', 'fecha credito', 'fecha crédito', 'date']),
            row['valor prestamo'],
            row['saldo']
          ];

          return requiredCandidates.some(hasMeaningfulValue);
        });

        setRawFileRows(renamed);
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error reading file:', error);
      showAlert('Error al leer el archivo', 'error');
    } finally {
      hideLoading();
    }
  };


  const handleSubmitData = async () => {
    if (!canCreate) {
      showAlert('Sin permiso para guardar importaciones.', 'warning');
      return;
    }
    if (!collect_id) {
      showAlert('Selecciona un cobro válido antes de guardar', 'warning');
      return;
    }
    if (!Constantid) {
      showAlert('Selecciona una constante válida antes de guardar', 'warning');
      return;
    }
    const weeklyRowsWithoutRouteDay = fileRows.filter(
      (row) => row.is_weekly && (row.route_day === '' || row.route_day === undefined || row.route_day === null)
    );
    if (weeklyRowsWithoutRouteDay.length > 0) {
      showAlert(
        `Seleccione el día de cobro para las constantes semanales. Filas: ${weeklyRowsWithoutRouteDay
          .map((row) => row.rowNumber)
          .join(', ')}.`,
        'warning'
      );
      return;
    }
    if (importErrors.length > 0) {
      setErrorModalOpen(canViewErrors);
      showAlert('El archivo tiene errores. Revísalos antes de guardar.', 'warning');
      return;
    }
    if (fileRows.length === 0) {
      showAlert('No hay filas válidas para importar. Carga un archivo con datos antes de guardar.', 'warning');
      return;
    }
    try {
      showLoading();
      const payload = {
        rows: fileRows,
        collect_id,
        constant_id: Constantid,  // utilidad del 20%
      };
      const response = await axios.post('/process/importlend', payload);
      const data = response?.data ?? {};
      const responseErrors = getImportErrorsFromResponse(data);
      const importedRows = Array.isArray(data.resultados) ? data.resultados : [];
      const failedPostImportProcesses = Array.isArray(data.post_import_processes)
        ? data.post_import_processes.filter((process: any) => process.status === 'error')
        : [];

      if (responseErrors.length > 0) {
        setImportErrors(responseErrors);
        setErrorModalOpen(canViewErrors);
        showAlert(
          `${data.message || 'La importación devolvió errores de validación.'} Usa el botón "Ver errores" para revisar el detalle.`,
          'warning'
        );
        return;
      }

      if (importedRows.length === 0) {
        setImportErrors(responseErrors);
        setErrorModalOpen(canViewErrors && responseErrors.length > 0);
        showAlert(
          data.message || 'La importación no confirmó préstamos guardados. Revisa el archivo e inténtalo nuevamente.',
          responseErrors.length > 0 ? 'warning' : 'warning'
        );
        return;
      }

      if (failedPostImportProcesses.length > 0) {
        const processDetails = failedPostImportProcesses
          .map((process: any) => `${process.process_name}: ${process.error_message || 'sin detalle'}`)
          .join(' | ');

        console.warn('Procesos posteriores con error:', failedPostImportProcesses);
        showAlert(
          `${data.message || 'Importación completada con advertencias.'} Detalle: ${processDetails}`,
          'warning'
        );
      } else {
        showAlert(data.message || 'Importación realizada correctamente', 'success');
      }
      setImportErrors([]);
      setErrorModalOpen(false);
      setFileRows([]);
      setRawFileRows([]);
      // Resetear el input para permitir re-cargar otro archivo sin refrescar página.
      if (fileInputRef.current) fileInputRef.current.value = '';
      setFileInputKey(k => k + 1);
    } catch (error: any) {
      console.error('Error detallado:', error.response?.data || error.message || error);
      const errors = getImportErrorsFromResponse(error?.response?.data, true) || getImportErrors(error);
      const errorMessage = getImportErrorMessage(error);
      setImportErrors(errors);
      setErrorModalOpen(canViewErrors && errors.length > 0);
      showAlert(
        errors.length > 0
          ? `${errorMessage} Usa el botón "Ver errores" para revisar el detalle.`
          : errorMessage,
        errors.length > 0 ? 'warning' : 'error'
      );
    } finally {
      hideLoading();
    }
  };

  const handleChangeRowConstant = (rowId: number, newConstantId: number) => {
    const selectedConstant = constants.find((c) => c.id === Number(newConstantId));
    if (!selectedConstant) return;

    setFileRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? recalculateRowWithConstant(row, selectedConstant) : row
      )
    );
  };

  const handleChangeRowRouteDay = (rowId: number, routeDay: string) => {
    setFileRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, route_day: routeDay } : row
      )
    );
  };

  const columns: SimpleColumn[] = [
    { field: 'position', headerName: 'Posición', minWidth: 100 },
    { field: 'idnumber', headerName: 'Cédula', minWidth: 130 },
    { field: 'fullname', headerName: 'Nombre', minWidth: 260, wrapText: true },
    { field: 'cellphone', headerName: 'Teléfono', minWidth: 150 },
    { field: 'mainaddress', headerName: 'Dirección', minWidth: 240, wrapText: true },
    {
      field: 'total_value',
      headerName: 'Valor Total',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.total_value ?? 0))}</Typography>
      ),
    },
    {
      field: 'daily_pay_value',
      headerName: 'Cuota',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.daily_pay_value ?? 0))}</Typography>
      ),
    },
    {
      field: 'value',
      headerName: 'Préstamo',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.value ?? 0))}</Typography>
      ),
    },
    {
      field: 'constant_id',
      headerName: 'Constante',
      minWidth: 180,
      renderCell: ({ row }) => (
        <FormControl fullWidth size="small">
          <Select
            value={row.constant_id ?? Constantid ?? ''}
            onChange={(e) =>
              handleChangeRowConstant(
                Number(row.id),
                Number((e.target as any).value)
              )
            }
          >
            {constants.map((constant: Constant) => (
              <MenuItem key={constant.id} value={constant.id}>
                {constant.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      field: 'route_day',
      headerName: 'Día cobro',
      minWidth: 170,
      renderCell: ({ row }) => (
        <FormControl fullWidth size="small" disabled={!row.is_weekly}>
          <Select
            displayEmpty
            value={row.is_weekly ? (row.route_day ?? '') : ''}
            onChange={(e) =>
              handleChangeRowRouteDay(
                Number(row.id),
                String((e.target as any).value)
              )
            }
          >
            <MenuItem value="">
              <em>{row.is_weekly ? 'Seleccione día' : 'No aplica'}</em>
            </MenuItem>
            <MenuItem value="1">Lunes</MenuItem>
            <MenuItem value="2">Martes</MenuItem>
            <MenuItem value="3">Miércoles</MenuItem>
            <MenuItem value="4">Jueves</MenuItem>
            <MenuItem value="5">Viernes</MenuItem>
            <MenuItem value="6">Sábado</MenuItem>
            <MenuItem value="0">Domingo</MenuItem>
          </Select>
        </FormControl>
      ),
    },
    {
      field: 'utility',
      headerName: '% Utilidad',
      align: 'center',
      minWidth: 120,
      renderCell: ({ row }) => (
        <Typography variant="body2">{Number(row.utility ?? 0)}</Typography>
      ),
    },
    {
      field: 'value_utility',
      headerName: 'Valor Utilidad',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.value_utility ?? 0))}</Typography>
      ),
    },
    { field: 'date', headerName: 'Fecha', minWidth: 140 },
    {
      field: 'balance',
      headerName: 'Saldo',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.balance ?? 0))}</Typography>
      ),
    },
    {
      field: 'paid_total',
      headerName: 'Abonado',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.paid_total ?? 0))}</Typography>
      ),
    },
    {
      field: 'count_pay',
      headerName: 'No. Pagos',
      align: 'center',
      minWidth: 120,
      renderCell: ({ row }) => (
        <Typography variant="body2">{Number(row.count_pay ?? 0)}</Typography>
      ),
    },
  ];

  // cargar oficinas y cobros
  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const officesResponse = axios.get('offices');
        const collectsOfficeResponse = office_id ? axios.get(`collectsoffice/${office_id}`) : null;

        const [officesData, collectsOfficeData] = await Promise.all([
          officesResponse,
          collectsOfficeResponse,
        ]);

        if (officesData) setOffices(officesData.data.data);
        if (collectsOfficeData) setCollectsOffice(collectsOfficeData.data);
        setClients([]);
      } catch (error) {
        showAlert('Error al cargar oficinas o cobros', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchData();
  }, [office_id]);
  useEffect(() => {
    if (office_id) {
      const fetchConstants = async () => {
        showLoading();
        try {
          const { data } = await axios.get(`constantofficescomplete/${office_id}`);
          setConstants(data.constants)
        } catch (error) {
          showAlert('No se encontraron constantes asociadas a la oficina!', 'warning');
        } finally {
          hideLoading();
        }
      };

      fetchConstants();
    }
  }, [office_id]);
  useEffect(() => {
    const ClientesCollect = async () => {
      if (!collect_id) {
        showAlert('No existen cobros asociados a la oficina!', 'info');
        return;
      }
      showLoading();
      try {
        const { data } = await axios.get(`collectclient/${collect_id}`);
        setClients(data.data);
        showAlert('Clientes cargados correctamente!', 'info');
      } catch (error) {
        showAlert('Error cargando clientes!', 'error');
      } finally {
        hideLoading();
      }
    };

    ClientesCollect();
  }, [collect_id]);
  useEffect(() => {
    if (!constants || constants.length === 0) {
      setConstantid('');
      setDays_term('');
      setUtility('');
      setSelectedConstantIsWeekly(false);
      setSelectedTypeConstantId(null);
    }
  }, [constants]);
  useEffect(() => {
    if (rawFileRows.length === 0) {
      setFileRows([]);
      setImportErrors([]);
      setErrorModalOpen(false);
      return;
    }

    const importedRows = buildImportedRows(
      rawFileRows,
      utility,
      days_term,
      selectedTypeConstantId ?? undefined,
      Constantid ? Number(Constantid) : undefined
    );
    const validationErrors = validateImportedRows(importedRows);

    setFileRows(importedRows);
    setImportErrors(validationErrors);
    setErrorModalOpen(canViewErrors && validationErrors.length > 0);
  }, [rawFileRows, days_term, utility, selectedTypeConstantId, Constantid, canViewErrors]);

  return (
    <HeaderResponsive>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Importar Préstamos</Title>
        <Box display="flex" gap={1} ml="auto">
          <PermissionButton
            allowed={canRead}
            deniedTooltip="Sin permiso para ver historial"
            tooltip="Historial"
            variant="outlined"
            color="primary"
            size='small'
            component={NavLink}
            to="/procesos/importaciones"
          >
            Historial
          </PermissionButton>
          <PermissionButton
            allowed={canViewErrors}
            deniedTooltip="Sin permiso para ver errores"
            tooltip={`Ver errores (${importErrors.length})`}
            variant="outlined"
            color="error"
            startIcon={isMobile ? null : <InfoIcon />}
            size='small'
            disabled={importErrors.length === 0}
            onClick={() => setErrorModalOpen(true)}
          >
            {isMobile ? <InfoIcon /> : `Ver errores (${importErrors.length})`}
          </PermissionButton>
          <PermissionButton
            allowed={canCreate}
            deniedTooltip="Sin permiso para guardar"
            tooltip="Guardar"
            variant="contained"
            startIcon={isMobile ? null : <SaveIcon />}
            size='small'
            onClick={handleSubmitData}
          >
            {isMobile ? <SaveIcon /> : "Guardar"}
          </PermissionButton>
        </Box>

        <input
          id="fileInput"
          type="file"
          hidden
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
        />

      </Box>

      <Card
        sx={{
          width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto',
          height: '950px'
        }}>
        <form onSubmit={(e) => e.preventDefault()}>
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Seleccionar Oficina</InputLabel>
                  <Select
                    value={office_id || ''}
                    label="Seleccionar Oficina"
                    onChange={(e) => setOffice_id(e.target.value)}
                    required
                    size='small'
                  >
                    {offices.map((office) => (
                      <MenuItem key={office.id} value={office.id}>{office.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Seleccionar Cobro</InputLabel>
                  <Select
                    value={collect_id || ''}
                    label="Seleccionar Cobro"
                    onChange={(e) => setCollect_id(e.target.value)}
                    required
                    size='small'

                  >
                    {collectsOffice.map((collect) => (
                      <MenuItem key={collect.id} value={collect.id}>{collect.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="form-layouts-separator-select-label">
                    Seleccionar Constante *
                  </InputLabel>
                  {constants && constants.length > 0 ? (
                    <Select
                      label="Seleccionar Constante"
                      defaultValue=""
                      size="small"
                      id="constante_id"
                      labelId="constante_id"
                      onChange={(event) => {
                        const selectedConstantId = event.target.value;
                        setConstantid(selectedConstantId);

                        // Buscar el objeto de constante seleccionado
                        const selectedConstant = constants.find((constant: Constant) => constant.id === Number(selectedConstantId));
                        if (selectedConstant) {
                          setDays_term(selectedConstant.term.toString());
                          setUtility(selectedConstant.utility.toString());
                          setSelectedTypeConstantId(Number(selectedConstant.type_constant.id));
                          setSelectedConstantIsWeekly(selectedConstant.type_constant.id === 2);
                        }
                      }}
                      required
                    >
                      {constants.map((constant: Constant) => (
                        <MenuItem key={constant.id} value={constant.id}>
                          {constant.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
              {constants && constants.length > 0 && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Número de Pagos"
                      value={selectedPaymentCount}
                      type="number"
                      inputProps={{ readOnly: true }}
                      onChange={(event) => setDays_term(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="% Utilidad"
                      value={utility ?? ''}
                      type="number"
                      inputProps={{ readOnly: true }}
                      onChange={(event) => setUtility(event.target.value)}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Button variant="outlined" component="label">
                  Seleccionar archivo
                  <input
                    key={fileInputKey}
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".xlsx, .xls"
                    onChange={handleFileUpload}
                  />
                </Button>
              </Grid>

              {fileRows.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <SimpleCustomTable
                      rows={fileRows.map((row, index) => ({ id: index, ...row }))}
                      columns={columns}
                      maxHeight={500}
                      showBorders
                      showCount
                      exportCsv
                      exportFileName="importar-prestamos.csv"
                      wrapCellContent
                    />
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <PermissionButton
                        allowed={canCreate}
                        deniedTooltip="Sin permiso para guardar"
                        tooltip="Guardar"
                        variant="contained"
                        startIcon={isMobile ? null : <SaveIcon />}
                        color="primary"
                        onClick={handleSubmitData}
                      >
                        {isMobile ? <SaveIcon /> : "Guardar"}
                      </PermissionButton>
                    </Box>
                  </Grid>
                </>
              )}
              {fileRows.length === 0 && (
                <Grid item xs={12}>
                  <EmptyStateMessage
                    message="Aún no has cargado un archivo para importar préstamos."
                    icon={<InfoIcon />}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </form>
      </Card>

      <Modal open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
        <Paper sx={errorModalStyle}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              bgcolor: 'error.main',
              color: 'white',
            }}
          >
            <Typography variant="h6">Errores de importación</Typography>
            <IconButton
              onClick={() => setErrorModalOpen(false)}
              sx={{ color: 'white' }}
              aria-label="cerrar"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, overflowY: 'auto', flexGrow: 1 }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Se encontraron {importErrors.length} error{importErrors.length === 1 ? '' : 'es'} en el archivo.
            </Typography>

            <Box display="flex" flexDirection="column" gap={1.5}>
              {importErrors.map((error, index) => (
                <Box
                  key={`${index}-${error}`}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography variant="subtitle2" color="error.main" mb={0.5}>
                    Error {index + 1}
                  </Typography>
                  <Typography variant="body2">{error}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      </Modal>
    </HeaderResponsive>
  );
};

export default ImportarPrestamos;
